import React, { useEffect } from "react"
import { Button, Col, Modal, ModalBody, ModalHeader, Row } from "reactstrap"
import { useTranslation } from "react-i18next"
import barcodeImage from "../barcode.png"
import { FilterByDateRangeModal } from "components/Extra/FilterByDateRangeModal"

interface BarcodeModalProps {
  isOpen: boolean
  toggle: () => void
}

export const BarcodeModal: React.FC<BarcodeModalProps> = ({
  isOpen,
  toggle,
}) => {
  const { t } = useTranslation()

  // Generate mock data for demonstration - in production this would come from props or API
  const deliveredCoupons = 123456
  const barcodeCount = 35 // 7 rows x 5 columns

  useEffect(() => {
    // Add print styles dynamically
    const style = document.createElement('style')
    style.innerHTML = `
      @media print {
        @page {
          size: A4 portrait;
          margin: 1cm;
        }
        
        html, body {
          height: auto !important;
          overflow: visible !important;
        }
        
        /* Hide main page content */
        #root > div:not(.barcode-modal),
        .page-content,
        header,
        .vertical-menu,
        .navbar-header,
        .topnav,
        .footer {
          display: none !important;
        }
        
        .modal-backdrop {
          display: none !important;
        }
        
        /* Show and reset modal */
        .modal {
          display: block !important;
          position: static !important;
          overflow: visible !important;
        }
        
        .barcode-modal,
        .barcode-modal.fade,
        .barcode-modal.show {
          position: static !important;
          display: block !important;
          top: auto !important;
          left: auto !important;
          right: auto !important;
          bottom: auto !important;
          width: 100% !important;
          height: auto !important;
          overflow: visible !important;
          z-index: 0 !important;
          opacity: 1 !important;
          transform: none !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        
        .barcode-modal .modal-dialog {
          position: static !important;
          display: block !important;
          max-width: 100% !important;
          width: 100% !important;
          margin: 0 !important;
          height: auto !important;
          max-height: none !important;
          transform: none !important;
          padding: 0 !important;
        }
        
        .barcode-modal .modal-content {
          position: static !important;
          display: block !important;
          border: none !important;
          box-shadow: none !important;
          background: white !important;
          height: auto !important;
          max-height: none !important;
          overflow: visible !important;
          padding: 0 !important;
        }
        
        .barcode-modal .modal-header {
          border: none !important;
          padding-bottom: 1rem !important;
          page-break-after: avoid !important;
        }
        
        .barcode-modal .modal-header .btn-close {
          display: none !important;
        }
        
        .barcode-modal .modal-body {
          padding: 0 !important;
          margin: 0 !important;
          max-height: none !important;
          height: auto !important;
          overflow: visible !important;
        }
        
        .barcode-grid {
          max-height: none !important;
          overflow: visible !important;
          height: auto !important;
          display: block !important;
        }
        
        .barcode-grid .row {
          display: grid !important;
          grid-template-columns: repeat(5, 1fr) !important;
          gap: 0.3rem !important;
          max-height: none !important;
          overflow: visible !important;
        }
        
        .barcode-grid .col-2,
        .barcode-grid .col-lg-2,
        .barcode-grid .col-md-3,
        .barcode-grid .col-sm-4,
        .barcode-grid .col-xs-6,
        .barcode-grid [class*="col-"] {
          width: 100% !important;
          max-width: 100% !important;
          flex: none !important;
          padding: 0 !important;
        }
        
        .barcode-item {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          -webkit-column-break-inside: avoid !important;
          display: block !important;
          padding: 0.2rem !important;
          margin: 0 !important;
        }
        
        .barcode-item img {
          width: 100% !important;
          max-width: 100% !important;
          height: auto !important;
          display: block !important;
        }
        
        /* Hide footer during print */
        .border-top {
          display: none !important;
        }
      }
    `
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  const handlePrint = () => {
    window.print()
  }

  const handlePrintAsPdf = () => {
    // In production, this would generate and download a PDF
    window.print()
  }

  const onDateRangeChange = (startDate: string, endDate: string) => {
    console.log("Selected date range:", startDate, "to", endDate)
  }


  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" className="barcode-modal">
      <ModalHeader toggle={toggle} tag="h3">
        <h4 className="text-capitalize fw-semibold font-size-24 mb-0">
          {t("deliveryMen_generateDeliveredCouponBarcode")}
        </h4>
      </ModalHeader>
      <ModalBody className="p-4">
        {/* Barcode Grid */}
        <div className="d-flex mb-2 gap-2">
          <span>{t("common_dateRange")} :</span>
          <FilterByDateRangeModal
            onDateRangeChange={onDateRangeChange}
            className="justify-content-start"
            style={{ flex: "none" }}
          />
        </div>

        <div
          className="barcode-grid mb-4"
          style={{ maxHeight: "60vh", overflowY: "auto", overflowX: "hidden" }}
        >
          <Row className="g-3">
            {Array.from({ length: barcodeCount }).map((_, index) => (
              <Col
                key={index}
                xs="6"
                sm="4"
                md="3"
                lg="2"
                className="d-flex justify-content-center"
              >
                <div className="barcode-item text-center border rounded">
                  <img
                    src={barcodeImage}
                    alt="Aspose BarCode"
                    style={{ width: "100%", height: "auto", maxWidth: "150px" }}
                    className="mb-1"
                  />
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Footer Section */}
        <Row className="align-items-center mt-4 pt-3 border-top">
          <Col xs="12" md="6" className="mb-3 mb-md-0">
            <div className="d-flex gap-2">
              <Button
                color="primary"
                onClick={handlePrint}
                className="bg-gradiant-blue text-capitalize btn btn-secondary border-0 px-2"
              >
                {t("deliveryMen_print")}
              </Button>
              <Button color="primary" outline onClick={handlePrintAsPdf}>
                {t("deliveryMen_printAsPdf")}
              </Button>
            </div>
          </Col>
          <Col xs="12" md="6">
            <div className="text-md-end">
              <span className="text-primary fw-semibold font-size-16">
                {t("deliveryMen_deliveredCoupons")} :{" "}
                {deliveredCoupons.toLocaleString()}
              </span>
            </div>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  )
}
